import React, { useEffect, useState } from 'react';

import styled from 'styled-components';

interface SwipeIndicatorProps {
  containerRef: React.RefObject<HTMLDivElement>;
  variant?: 'secondary'; // if not provided, will use default styling
  onPageChange?: (page: number) => void;
  showPageText?: boolean; // New prop to control page text visibility
}

interface PageIndicatorProps {
  isActive: boolean;
  variant?: 'secondary';
  onClick?: () => void;
}

const Container = styled.div<{ $variant?: 'secondary' }>`
  width: auto;
  height: 21px;
  position: relative;
  margin: 0 0 0 auto;
  display: flex;
  justify-content: ${({ $variant }) => ($variant === 'secondary' ? 'center' : 'flex-end')};
  z-index: 10;

  @media (max-width: 768px) {
    justify-content: ${({ $variant }) => ($variant === 'secondary' ? 'center' : 'flex-start')};
  }
`;

const IndicatorWrapper = styled.div`
  display: inline-flex;
  align-self: stretch;
  justify-content: flex-start;
  align-items: center;
  gap: 9px;
`;

const DotsWrapper = styled.div`
  padding-left: 3px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 9px;
`;

const ClickableDot = styled.button<{ $isActive: boolean; $variant?: 'secondary' }>`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const ArrowButton = styled.button<{ $variant?: 'secondary' }>`
  background: none;
  border: none;
  padding: 0;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  transition: opacity 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translateY(2px); // Move arrows down slightly to align with dots

  &:hover:not(:disabled) {
    opacity: 0.7;
  }

  svg {
    width: 16px;
    height: 16px;
    fill: ${({ disabled, $variant }) => {
      if (disabled) return $variant === 'secondary' ? 'var(--stile-grey-3)' : 'var(--stile-grey-4)';
      return $variant === 'secondary' ? 'var(--white)' : 'var(--stile-grey-6)';
    }};
  }
`;

const PageIndicator: React.FC<PageIndicatorProps> = ({ isActive, variant, onClick }) => {
  const activeStroke = variant === 'secondary' ? 'var(--white)' : 'var(--stile-green-3)';
  const inactiveFill = variant === 'secondary' ? 'var(--stile-green-1)' : 'var(--stile-grey-6)';

  const svg = isActive ? (
    <svg width="26" height="7" viewBox="0 0 26 7" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 3.5H23" stroke={activeStroke} strokeWidth="6" strokeLinecap="round" />
    </svg>
  ) : (
    <svg width="6" height="7" viewBox="0 0 6 7" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="3" cy="3.5" r="3" fill={inactiveFill} />
    </svg>
  );

  // Only make non-active dots clickable
  return !isActive && onClick ? (
    <ClickableDot $isActive={isActive} $variant={variant} onClick={onClick}>
      {svg}
    </ClickableDot>
  ) : (
    <div>{svg}</div>
  );
};

const PageText = styled.p<{ $variant?: 'secondary' }>`
  color: ${({ $variant }) => ($variant === 'secondary' ? 'var(--white)' : 'var(--stile-grey-6)')};
  width: auto;
  font-size: 14px;
  white-space: nowrap;
`;

const SwipeIndicator: React.FC<SwipeIndicatorProps> = ({
  containerRef,
  variant,
  onPageChange,
  showPageText = false, // Default to hidden
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const scrollToPage = (page: number) => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const scrollPosition = (page - 1) * containerWidth;
      containerRef.current.scrollTo({ left: scrollPosition, behavior: 'smooth' });
      if (onPageChange) {
        onPageChange(page);
      }
    }
  };

  const scrollLeft = () => {
    if (currentPage > 1) {
      scrollToPage(currentPage - 1);
    }
  };

  const scrollRight = () => {
    if (currentPage < totalPages) {
      scrollToPage(currentPage + 1);
    }
  };

  useEffect(() => {
    const updatePage = () => {
      if (containerRef.current) {
        const { scrollLeft, scrollWidth, offsetWidth } = containerRef.current;
        const containerWidth = offsetWidth || 1;
        // Calculate total pages (rounding up to include fractional pages)
        const calculatedTotalPages = Math.ceil(scrollWidth / containerWidth);
        setTotalPages(calculatedTotalPages);
        // Calculate current page (ensuring it does not exceed total pages)
        const newPage = Math.min(
          calculatedTotalPages,
          Math.ceil((scrollLeft + containerWidth) / containerWidth)
        );
        setCurrentPage(newPage);
      }
    };

    updatePage();
    const currentContainer = containerRef.current;
    if (currentContainer) {
      currentContainer.addEventListener('scroll', updatePage);
      return () => currentContainer.removeEventListener('scroll', updatePage);
    }
  }, [containerRef]);

  // Hide the component if there's only one page
  if (totalPages <= 1) return null;

  return (
    <Container $variant={variant}>
      <IndicatorWrapper>
        <ArrowButton
          onClick={scrollLeft}
          disabled={currentPage <= 1}
          $variant={variant}
          aria-label="Previous page"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 9.1875H5.11188L9.65375 4.64562L8.5 3.5L2 10L8.5 16.5L9.64563 15.3544L5.11188 10.8125H18V9.1875Z" />
          </svg>
        </ArrowButton>
        <DotsWrapper>
          {Array.from({ length: totalPages }).map((_, index) => (
            <PageIndicator
              key={index}
              isActive={index === currentPage - 1}
              variant={variant}
              onClick={() => scrollToPage(index + 1)}
            />
          ))}
        </DotsWrapper>
        <ArrowButton
          onClick={scrollRight}
          disabled={currentPage >= totalPages}
          $variant={variant}
          aria-label="Next page"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 10.8125L14.8881 10.8125L10.3463 15.3544L11.5 16.5L18 10L11.5 3.5L10.3544 4.64562L14.8881 9.1875L2 9.1875L2 10.8125Z" />
          </svg>
        </ArrowButton>
        {showPageText && (
          <PageText $variant={variant}>{`${currentPage} of ${totalPages}`}</PageText>
        )}
      </IndicatorWrapper>
    </Container>
  );
};

export default SwipeIndicator;
